import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import AppShell from './components/layout/AppShell';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import PrivacyPolicyPage from './pages/auth/PrivacyPolicyPage';
import SsoCallback from './pages/auth/SsoCallback';
import StudentDashboard from './pages/student/StudentDashboard';
import StudentSettings from './pages/student/StudentSettings';
import WorkshopView from './pages/student/WorkshopView';
import InstructorDashboard from './pages/instructor/InstructorDashboard';
import CreateWorkshop from './pages/instructor/CreateWorkshop';
import WorkshopEditor from './pages/instructor/WorkshopEditor';
import WorkshopPreview from './pages/instructor/WorkshopPreview';
import SessionList from './pages/instructor/SessionList';
import CreateSession from './pages/instructor/CreateSession';
import SessionActive from './pages/instructor/SessionActive';
import LiveConsole from './pages/instructor/LiveConsole';
import HelpQueue from './pages/instructor/HelpQueue';
import SessionSummary from './pages/instructor/SessionSummary';
import PresentationMode from './pages/instructor/PresentationMode';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminWorkshops from './pages/admin/AdminWorkshops';
import AdminSessions from './pages/admin/AdminSessions';
import AdminSettings from './pages/admin/AdminSettings';
import AdminAudit from './pages/admin/AdminAudit';
import AdminDesktops from './pages/admin/AdminDesktops';
import AdminFeedback from './pages/admin/AdminFeedback';
import AdminUserDetail from './pages/admin/AdminUserDetail';
import AdminWorkshopDetail from './pages/admin/AdminWorkshopDetail';
import AdminSessionDetail from './pages/admin/AdminSessionDetail';
import DocumentationPage from './pages/common/DocumentationPage';
import NotFoundPage from './pages/NotFoundPage';
import HelpNotificationManager from './components/instructor/HelpNotificationManager';

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NotificationProvider>
        <HelpNotificationManager />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/sso/callback" element={<SsoCallback />} />

          {/* Student routes */}
          <Route path="/student" element={
            <ProtectedRoute role="student"><AppShell /></ProtectedRoute>
          }>
            <Route index element={<StudentDashboard />} />
            <Route path="settings" element={<StudentSettings />} />
            <Route path="documentation" element={<DocumentationPage />} />
          </Route>
          <Route
            path="/student/session/:sessionId"
            element={
              <ProtectedRoute role="student">
                <WorkshopView />
              </ProtectedRoute>
            }
          />

          {/* Instructor routes */}
          <Route path="/instructor" element={
            <ProtectedRoute role="instructor"><AppShell /></ProtectedRoute>
          }>
            <Route index element={<InstructorDashboard />} />
            <Route path="workshop/new" element={<CreateWorkshop />} />
            <Route path="sessions" element={<SessionList />} />
            <Route path="sessions/new" element={<CreateSession />} />
            <Route path="session/:id" element={<SessionActive />} />
            <Route path="session/:id/help" element={<HelpQueue />} />
            <Route path="session/:id/summary" element={<SessionSummary />} />
            <Route path="live/:id" element={<LiveConsole />} />
            <Route path="documentation" element={<DocumentationPage />} />
          </Route>
          {/* Standalone instructor pages (full-screen, no AppShell) */}
          <Route
            path="/instructor/session/:sessionId/present"
            element={
              <ProtectedRoute role="instructor">
                <PresentationMode />
              </ProtectedRoute>
            }
          />
          <Route
            path="/instructor/workshop/:id/edit"
            element={
              <ProtectedRoute role="instructor">
                <WorkshopEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/instructor/workshop/:id/preview"
            element={
              <ProtectedRoute role="instructor">
                <WorkshopPreview />
              </ProtectedRoute>
            }
          />

          {/* Admin routes */}
          <Route path="/admin" element={
            <ProtectedRoute role="admin"><AppShell /></ProtectedRoute>
          }>
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="users/:id" element={<AdminUserDetail />} />
            <Route path="workshops" element={<AdminWorkshops />} />
            <Route path="workshops/:id" element={<AdminWorkshopDetail />} />
            <Route path="sessions" element={<AdminSessions />} />
            <Route path="sessions/:id" element={<AdminSessionDetail />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="audit" element={<AdminAudit />} />
            <Route path="desktops" element={<AdminDesktops />} />
            <Route path="feedback" element={<AdminFeedback />} />
            <Route path="documentation" element={<DocumentationPage />} />
          </Route>

          <Route path="/" element={<Navigate to="/login" />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </NotificationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
